import { useEffect, useState, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { useErrorHandler } from 'react-error-boundary';
import { AxiosRequestConfig, AxiosResponse } from 'axios';
import axios from '.';
import { camelizeResponse, decamelizeRequest } from './case-transforms';
import {
  useAppDispatch,
  logout,
  setMaxRetriesError,
  setAdminTakeoverCompanyUser,
  setShowTfaSetupModal,
} from '../../store';
import { refreshToken } from '../auth';
import {
  ACCESS_TOKEN_STORAGE_KEY,
  ADMIN_TAKEOVER_TOKEN_STORAGE_KEY,
  BANKING,
  CURRENT_IMPERSONATED_COMPANY_UUID,
  REFRESH_TOKEN_STORAGE_KEY,
  TFA_VALIDATED_STORAGE_KEY,
  TWO_FACTOR_AUTHENTICATION,
} from '../../constants/auth';
import { BANKING_NOT_STARTED_ERROR_CODE } from '../../constants/banking';
import { useSnackbar } from '../../context';
import { resetStore } from '../../utils/helpers';

export const HTTPInterceptor = () => {
  const [responseInterceptor, setResponseInterceptor] = useState(0);
  const [requestInterceptor, setRequestInterceptor] = useState(0);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const handleError = useErrorHandler();
  const { showSnackbar } = useSnackbar();

  const logoutUser = () => {
    return dispatch(logout())
      .unwrap()
      .then(() => {
        dispatch(setMaxRetriesError(true));
        navigate('/login');
        resetStore(dispatch);
      });
  };

  const addRequestInterceptor = () => {
    const requestInterceptor = axios.interceptors.request.use(async (config: AxiosRequestConfig) => {
      if (!config.headers) {
        config.headers = {};
      }
      const token = localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY);
      const adminTakeoverToken = localStorage.getItem(ADMIN_TAKEOVER_TOKEN_STORAGE_KEY);

      config.headers = {
        ...config.headers, // Keep existing headers
        ...(token && { Authorization: `Bearer ${token}` }),
        ...(adminTakeoverToken && { 'Impersonation-Token': adminTakeoverToken }),
      };

      return decamelizeRequest(config);
    });
    setRequestInterceptor(requestInterceptor);
  };

  const removeRequestInterceptor = () => {
    axios.interceptors.request.eject(requestInterceptor);
    setRequestInterceptor(0);
  };

  const addResponseInterceptor = () => {
    const responseInterceptor = axios.interceptors.response.use(
      (response: AxiosResponse) => camelizeResponse(response),
      async (error) => {
        if (error.response) {
          // If the response is a 404 there is no point on retrying
          if (error.response.status === 404) {
            return Promise.reject(error);
          }
          // The request was made and the server responded with a status code
          // that falls out of the range of 2xx
          const {
            response: { status },
          } = error || {};
          const originalRequest = error.config;
          const token = localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
          const adminTakeoverToken = localStorage.getItem(ADMIN_TAKEOVER_TOKEN_STORAGE_KEY);
          if (status === 403 && adminTakeoverToken) {
            showSnackbar(
              `Sorry you don't have the permission to do this action. If you think this is a mistake, please contact Caleb`,
              'error'
            );
          }
          if (status === 401 && adminTakeoverToken) {
            localStorage.removeItem(ADMIN_TAKEOVER_TOKEN_STORAGE_KEY);
            const companyUuid = localStorage.getItem(CURRENT_IMPERSONATED_COMPANY_UUID);
            localStorage.removeItem(CURRENT_IMPERSONATED_COMPANY_UUID);
            dispatch(setAdminTakeoverCompanyUser(null));
            window.location.replace(companyUuid ? `/companies/${companyUuid}` : '/dashboard');
            return;
          }
          if (error.response && status === 401 && error.config.url === '/auth/token/refresh/') {
            return logoutUser();
          } else {
            if (error.response.data.error_code === 'two_factor_authentication_invalid_code') {
              const twoFactor = window.prompt('Please input your 2FA code');
              if (twoFactor) {
                originalRequest.headers['X-2FA-TOKEN'] = twoFactor;
                return axios(originalRequest);
              } else {
                // Handle the case when user cancels the prompt
                console.log('2FA prompt cancelled by the user.');
                return Promise.reject(error);
              }
            } else if (error.response.data.error_code === 'two_factor_authentication_required') {
              localStorage.setItem(TFA_VALIDATED_STORAGE_KEY, 'false');
              dispatch(setShowTfaSetupModal(false));
              navigate(`${TWO_FACTOR_AUTHENTICATION}`);
              return Promise.reject(error);
            } else if (error.response.data.error_code === 'two_factor_authentication_setup_required') {
              // User must configure 2FA first: open the setup modal instead of
              // sending them to the code-entry screen they can't complete.
              dispatch(setShowTfaSetupModal(true));
              return Promise.reject(error);
            } else if (error.response.data.error_code === BANKING_NOT_STARTED_ERROR_CODE) {
              navigate(BANKING);
              return Promise.reject(error);
            }
            if (error.response.status === 401 && token) {
              const { access, refresh } = await refreshToken(token);
              localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, access);
              localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, refresh);
              return axios(originalRequest);
            }
            if (status.toString()[0] === '5') {
              // Skip error boundary for aging_history, export, and QBO connection refresh
              // endpoints to allow component-level error handling
              if (
                !originalRequest.url.includes('/aging_history/') &&
                !originalRequest.url.includes('/aging_history/export/') &&
                !originalRequest.url.includes('/products/invoicing/connection/refresh/')
              ) {
                handleError(error);
              }
            }
            return Promise.reject(error);
          }
        } else if (error.request) {
          // The request was made but no response was received
          logoutUser();
          return Promise.reject(error);
        } else {
          // Something happened in setting up the request that triggered an Error
          logoutUser();
          return Promise.reject(error);
        }
      }
    );
    setResponseInterceptor(responseInterceptor);
  };

  const removeResponseInterceptor = () => {
    axios.interceptors.response.eject(responseInterceptor);
    setResponseInterceptor(0);
  };

  useEffect(() => {
    addRequestInterceptor();
    addResponseInterceptor();
    return () => {
      removeResponseInterceptor();
      removeRequestInterceptor();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <Fragment />;
};
